import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";

const ToolSeoHero = ({
  badge = "Hookora Tool",
  title,
  description,
  highlights = [],
  primaryCtaLabel = "Start Free",
  primaryCtaTo = "/register",
  secondaryCtaLabel = "See Features",
  secondaryCtaTo = "/features",
  previewTitle = "What this tool helps you do",
  previewItems = []
}) => {
  const { isDark } = useThemeMode();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);

  return (
    <section
      style={{
        padding: "84px 20px 44px",
        background: colors.heroBg
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "34px",
          alignItems: "center"
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 14px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontWeight: "700",
              fontSize: "12px",
              marginBottom: "18px",
              border: isDark ? `1px solid ${colors.border}` : "none"
            }}
          >
            {badge}
          </div>

          <h1
            style={{
              fontSize: "clamp(2.3rem, 5vw, 4rem)",
              lineHeight: "1.04",
              margin: "0 0 16px",
              color: colors.text,
              letterSpacing: "-0.03em"
            }}
          >
            {title}
          </h1>

          <p
            style={{
              fontSize: "17px",
              lineHeight: "1.75",
              color: colors.textSoft,
              margin: "0 0 24px",
              maxWidth: "680px"
            }}
          >
            {description}
          </p>

          {highlights.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                marginBottom: "24px"
              }}
            >
              {highlights.map((item) => (
                <span
                  key={item}
                  style={{
                    background: colors.tagBg,
                    border: `1px solid ${colors.border}`,
                    color: colors.tagText,
                    borderRadius: "999px",
                    padding: "9px 12px",
                    fontSize: "13px",
                    fontWeight: "700"
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap"
            }}
          >
            <Link
              to={primaryCtaTo}
              style={{
                textDecoration: "none",
                background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                color: "#fff",
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                boxShadow: "0 12px 28px rgba(79, 70, 229, 0.24)"
              }}
            >
              {primaryCtaLabel}
            </Link>

            <Link
              to={secondaryCtaTo}
              style={{
                textDecoration: "none",
                background: colors.panelBg,
                color: colors.text,
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                border: `1px solid ${colors.borderStrong}`
              }}
            >
              {secondaryCtaLabel}
            </Link>
          </div>
        </div>

        <div>
          <div
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: "26px",
              padding: "22px",
              boxShadow: colors.shadow,
              backdropFilter: isDark ? "blur(14px)" : "none"
            }}
          >
            <div style={{ marginBottom: "14px" }}>
              <div style={{ color: colors.text, fontWeight: "800", fontSize: "18px" }}>
                {previewTitle}
              </div>
              <div style={{ color: colors.textMuted, fontSize: "14px", marginTop: "4px" }}>
                Clearer workflow, stronger output
              </div>
            </div>

            <div style={{ display: "grid", gap: "12px" }}>
              {previewItems.map((item, index) => (
                <div
                  key={`${item.label}-${index}`}
                  style={{
                    background: colors.cardBgSoft,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "18px",
                    padding: "16px"
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      padding: "5px 9px",
                      borderRadius: "999px",
                      background: colors.panelBg,
                      border: `1px solid ${colors.border}`,
                      color: colors.text,
                      fontSize: "12px",
                      fontWeight: "800",
                      marginBottom: "8px"
                    }}
                  >
                    {item.label}
                  </div>
                  <div style={{ color: colors.textSoft, lineHeight: "1.65", fontSize: "14px" }}>
                    {item.text}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: "14px",
                paddingTop: "14px",
                borderTop: `1px solid ${colors.border}`,
                color: colors.textMuted,
                fontSize: "13px",
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
                flexWrap: "wrap"
              }}
            >
              <span>Faster ideation</span>
              <span>Stronger wording</span>
              <span>Less friction</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToolSeoHero;